import React, { useState, useEffect } from 'react'
import { Users, Plus, Pencil, Trash2, X, Search } from 'lucide-react'
import { teamsService, Team, TeamCreate, TeamUpdate } from '../services/teams'
import { departmentsService, Department } from '../services/departments'
import { toast } from 'sonner'
import { getErrorMessage } from '../utils/apiTransformers'
import type { ApiErrorLike } from '../utils/apiError'

const EMPTY_FORM = { name: '', description: '', department_id: 0 }

export default function TeamsPage() {
  const [teams, setTeams] = useState<Team[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [formData, setFormData] = useState<typeof EMPTY_FORM>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    load()
  }, [])

  const load = async () => {
    setLoading(true)
    try {
      const [teamsData, deptsData] = await Promise.all([
        teamsService.list(),
        departmentsService.list(),
      ])
      setTeams(teamsData)
      setDepartments(deptsData)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      toast.error(getErrorMessage(error) || 'Failed to load teams')
    } finally {
      setLoading(false)
    }
  }

  const openCreate = () => {
    setEditingId(null)
    setFormData({ ...EMPTY_FORM, department_id: departments[0]?.id || 0 })
    setShowModal(true)
  }

  const openEdit = (team: Team) => {
    setEditingId(team.id)
    setFormData({ name: team.name, description: team.description || '', department_id: team.department_id })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) { toast.error('Name is required'); return }
    if (!formData.department_id) { toast.error('Department is required'); return }
    setSaving(true)
    try {
      if (editingId) {
        const update: TeamUpdate = {
          name: formData.name,
          description: formData.description,
          department_id: formData.department_id,
        }
        await teamsService.update(editingId, update)
        toast.success('Team updated')
      } else {
        await teamsService.create(formData as TeamCreate)
        toast.success('Team created')
      }
      setShowModal(false)
      load()
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      toast.error(getErrorMessage(error) || 'Failed to save team')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`Delete team "${name}"? This cannot be undone.`)) return
    try {
      await teamsService.delete(id)
      toast.success('Team deleted')
      load()
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      toast.error(getErrorMessage(error) || 'Failed to delete team')
    }
  }

  const getDeptName = (deptId: number) =>
    departments.find(d => d.id === deptId)?.name || `Department ${deptId}`

  const filtered = teams.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    (t.description || '').toLowerCase().includes(search.toLowerCase())
  )

  return (
    <>
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="w-7 h-7 text-[#486581]" />
          <div>
            <h1 className="text-2xl font-bold text-[#102a43]">Teams</h1>
            <p className="text-sm text-[#829ab1]">{teams.length} team{teams.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button
          onClick={openCreate}
          disabled={departments.length === 0}
          title={departments.length === 0 ? 'Create a department first' : undefined}
          className="flex items-center gap-2 px-4 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          New Team
        </button>
      </div>

      {departments.length === 0 && !loading && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          Teams require a department. <a href="/departments" className="underline font-medium">Create a department</a> first.
        </div>
      )}

      <div className="mb-4 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#829ab1]" />
        <input
          type="text"
          placeholder="Search teams..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 border border-[#bcccdc] rounded-lg text-sm text-[#102a43] bg-white focus:outline-none focus:ring-2 focus:ring-[#486581]"
        />
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-20 bg-[#f0f4f8] rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <Users className="w-12 h-12 text-[#829ab1] mx-auto mb-3" />
          <p className="text-[#829ab1]">{search ? 'No teams match your search' : 'No teams yet'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(team => (
            <div key={team.id} className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-sm p-4 hover:border-[#486581] hover:shadow-md transition-all">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-[#486581] flex-shrink-0" />
                    <h3 className="font-semibold text-[#102a43] truncate">{team.name}</h3>
                    <span className="text-xs px-2 py-0.5 bg-[#f0f4f8] text-[#486581] rounded border border-[#bcccdc] flex-shrink-0">
                      {getDeptName(team.department_id)}
                    </span>
                  </div>
                  {team.description && (
                    <p className="text-sm text-[#829ab1] ml-6">{team.description}</p>
                  )}
                  <p className="text-xs text-[#bcccdc] ml-6 mt-1">
                    Created {new Date(team.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => openEdit(team)}
                    aria-label={`Edit ${team.name}`}
                    title="Edit"
                    className="p-1.5 text-[#829ab1] hover:text-[#486581] hover:bg-[#f0f4f8] rounded transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(team.id, team.name)}
                    aria-label={`Delete ${team.name}`}
                    title="Delete"
                    className="p-1.5 text-[#829ab1] hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    {showModal && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
          <div className="p-5 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[#102a43]">
              {editingId ? 'Edit Team' : 'New Team'}
            </h2>
            <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <label htmlFor="team-name" className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
              <input
                id="team-name"
                type="text"
                required
                value={formData.name}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] text-gray-900"
              />
            </div>
            <div>
              <label htmlFor="team-department" className="block text-xs font-medium text-gray-700 mb-1">Department *</label>
              <select
                id="team-department"
                value={formData.department_id}
                onChange={e => setFormData(p => ({ ...p, department_id: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] text-gray-900"
              >
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="team-description" className="block text-xs font-medium text-gray-700 mb-1">Description</label>
              <textarea
                id="team-description"
                value={formData.description || ''}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] resize-none text-gray-900"
              />
            </div>
            <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
              <button type="button" onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 text-sm bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] disabled:opacity-50 flex items-center gap-2">
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Plus className="w-4 h-4" />}
                {editingId ? 'Save Changes' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    </>
  )
}
